import {
  css, html
} from "lit";

/* WARNING: Do NOT import directly from "braintree-web", it causes the bundle size to increase dramatically. */
import { customElement, state, property } from "lit/decorators.js";
import hostedFields, { HostedFieldsState, HostedFieldsTokenizePayload, HostedFields } from "braintree-web/hosted-fields";
import threeDSecure ,{ ThreeDSecure, ThreeDSecureVerifyPayload } from "braintree-web/three-d-secure";
import store from "../../../store/store";
import { StoreState } from "../../../store/store.interface";
import { BaseComponent } from "../../../features/base-component/base-component";
import { booleanConverter } from "../../../common/converters";
import { BraintreeHostedFieldsStatus, CreditCardDisplay, PaymentDetails, PaymentType } from "../../checkout/shop-checkout.interface";
import { isTunnelPurchaseFlow, wait } from "../../../common/utils/util";
import { getVaultedCardPaymentNonce } from "./vaulted-card-payment-nonce";
import { getBillingAddress, updateBraintreeHostedFieldsStatus } from "../../checkout/shop-checkout";
import { waitForElementsToLoad } from "../../../common/utils/delay-util";
import { isCreditCardFormLoaded, setCreditCardFormLoadingState } from "../braintree";
import { isValidBillingAddress } from "../../../common/utils/payment-util";
import { getCart, getIntlTaxAmount } from "../../cart/shop-cart/store/shop-cart.selectors";
import { CREDIT_CARD_FIELDS } from "../braintree.interface";

/**
 * Braintree credit card hosted fields component. Capture credit card information from user. 
 * Validate the credit card fields.
 * Tokenize credit card info and return the tokenized payload.
*/
@customElement("mhecomm-credit-card-payment-hosted-fields")
export default class BraintreeHostedFields extends BaseComponent {

  static styles = [
    css`
      .form-control{
        height: 35px;
      }
      form .form-control.braintree-hosted-fields-focused {
        border-color:var(--theme-red-400);
        outline: var(--theme-red-500) solid 2px;
        box-shadow: none;
        outline-offset: 2px;
      }
       `
  ];

  /**
   * Transaction amount is required for 3dSecure processing for verifying credit card.
   */
  @property({type: Number })
  amount = 0;

  /**
   * Credit card unique ID is required to create hosted fields for vaulted credit card
   */
  @property()
  creditCardUniqueId = '';

  /**
   * Vaulted credit card Payment Nonce required for 3d Secure processing
   */
  @property()
  vaultedCreditCardNonce = '';

  /**
   * Vaulted credit card BIN (bank identification number) required for 3d Secure processing
   */
  @property()
  bin = '';

  /**
   * Flag to enable or disable 3d Secure processing
   */
  @property({ converter: booleanConverter })
  threeDsEnabled = false;

  /**
   * Client token is a signed JWT required for config and authorization of Braintree javascript library instances
   */
  @property()
  clientToken = '';

  @state()
  private fieldToFocus = 'cvv';

  @state() 
  private threeDSCancelErrorMsg = 'Credit Card tokenization cancelled.';

  private braintreeHostedFieldsInstance: HostedFields | undefined;
  private braintreeThreeDSecureInstance: ThreeDSecure | undefined;
  private paymentDetails: PaymentDetails = { paymentType: PaymentType.BRAINTREE_CREDIT_CARD, saveInVault: false };
  private creditCardDisplayInfo: CreditCardDisplay | undefined;

  connectedCallback(){
    super.connectedCallback();
    if (this.threeDsEnabled) {
      this.getVaultedCardPaymentNonce();
      this.setupThreeDSecureInstance();
    }
  }

  async getVaultedCardPaymentNonce() {
    if(isTunnelPurchaseFlow() && !!this.creditCardUniqueId) {
      await store.dispatch(getVaultedCardPaymentNonce(this.creditCardUniqueId));
    }
  }

  setupThreeDSecureInstance(){
    if (!this.clientToken) {
      store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
      console.error('No client token provided');
      return;
    }

    threeDSecure.create({
      authorization: this.clientToken,
      version: 2,
    }).then((threeDSecureInstance) => {
        threeDSecureInstance.on("lookup-complete", function (_data, next) {
          if(next){
            next();
          }
        });
        this.braintreeThreeDSecureInstance = threeDSecureInstance;
      })
      .catch((error) => {
        console.log("failed to create 3ds instance",error);
      });
  }

  renderComp() {
    if (this.creditCardUniqueId) {
      return this.renderCvvOnlyPaymentForm();
    } else {
      return this.renderNewCreditCardPaymentForm();
    }
  }

  /**
   * 
   * @returns template for vaulted credit card payment form
   */
  private renderCvvOnlyPaymentForm(){
    return html`
        <form method="post">
          <div class="w-50">
            <label for="cvv" class="form-label fw-semibold">CVV</label>
            <div id="cvv" class="form-control"></div>
            <p id="cvv-hosted-field-error" class="invalid-feedback">${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}Please enter a valid credit card CVV</p>
          </div>
        </form>
      `;
  }

  /**
   * 
   * @returns template for new credit card payment form
   */
  private renderNewCreditCardPaymentForm(){
    return html`
        <form method="post">
          <div class="mb-3">
            <label for="cc-name" class="form-label">Card Holder Name</label>
            <div id="cc-name" class="form-control" aria-label="Credit Card Name"></div>
            <p id="cc-name-hosted-field-error" 
               role="alert" aria-live="assertive" 
               class="invalid-feedback">
              ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}<span class="visually-hidden">Error&nbsp;</span>Please enter a valid credit card name
            </p>
          </div>
          <div class="mb-3">
            <label for="card-number" class="form-label" aria-label="Credit Card Number">Credit Card Number</label>
            <div id="card-number" class="form-control"></div>
            <p id="card-number-hosted-field-error" 
               role="alert" aria-live="assertive" 
               class="invalid-feedback">
              ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}<span class="visually-hidden">Error&nbsp;</span>Please enter a valid credit card number
            </p>
          </div>
          <div class="row">
            <div class="mb-3 col-sm-6 col-md-6">
              <label for="expiration-date" class="form-label">Expiration Date (mm/yyyy)</label>
              <div id="expiration-date" class="form-control" aria-label="Expiration Date mm/yyyy"></div>
              <p id="expiration-date-hosted-field-error" 
                 role="alert" aria-live="assertive" 
                 class="invalid-feedback">
                ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}<span class="visually-hidden">Error&nbsp;</span>Please enter a valid credit card expiry date
              </p>
            </div>
            <div class="mb-3 col-sm-6 col-md-6">
              <label for="cvv" class="form-label">CVV</label>
              <div id="cvv" class="form-control" aria-label="CVV" ></div>
              <p id="cvv-hosted-field-error" 
                 role="alert" 
                 aria-live="assertive" 
                 class="invalid-feedback">
                ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}<span class="visually-hidden">Error&nbsp;</span>Please enter a valid credit card CVV
              </p>
            </div>
          </div>
        </form>
      `;
  }

  /**
   * After payment form has rendered, setup braintree hosted fields 
   * in the payment form placeholders
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await waitForElementsToLoad(this.shadowRoot!, [
      "#cc-name",
      "#card-number",
      "#expiration-date",
      "#cvv",
    ]);
    //load hosted fields
    this.setupBraintreeHostedFields();
  }

  private setupBraintreeHostedFields() {
    if (!this.clientToken) {
      store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
      console.error('No client token provided');
      return;
    }

    this.createHostedFields()
    .then((hostedFieldsInstance) => {
      hostedFieldsInstance._clientPromise.then(()=> {
        this.braintreeHostedFieldsInstance = hostedFieldsInstance;
        if(isCreditCardFormLoaded(store.getState() as StoreState)) {
          this.braintreeHostedFieldsInstance.focus(this.fieldToFocus);
          store.dispatch(setCreditCardFormLoadingState(false));
        }
        store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.VALID));
      }).catch((error) => {
        console.log("failed to create hosted fields instance",error);
        store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
      });
    });
  }

  private createHostedFields() {
    if (this.creditCardUniqueId) {
      this.fieldToFocus = 'cvv';
      return hostedFields.create({
        authorization: this.clientToken,
        fields: {
          cvv: {
            container: this.shadowRoot!.querySelector("#cvv") as HTMLElement,
            type: "password",
          },
        },
      });
    } else {
      this.fieldToFocus = 'cardholderName';
      return hostedFields.create({
        authorization: this.clientToken,
        fields: {
          cardholderName: {
            container: this.shadowRoot!.querySelector("#cc-name") as HTMLElement,
          },
          number: {
            container: this.shadowRoot!.querySelector("#card-number") as HTMLElement,
            placeholder: "**** **** **** ****",
          },
          cvv: {
            container: this.shadowRoot!.querySelector("#cvv") as HTMLElement,
            type: "password",
            placeholder: "***",
          },
          expirationDate: {
            container: this.shadowRoot!.querySelector("#expiration-date") as HTMLElement,
            placeholder: "mm/yyyy"
          }
        },
      });
    }
  }

  /**
   * Validate credit card fields
   */
  private validateCreditCard() {
    let isValid = true;
    const state: HostedFieldsState = this.braintreeHostedFieldsInstance!.getState();
    Object.keys(state.fields).forEach((key) => {
      const field = state.fields[key as keyof typeof state.fields];
      if (!field.isValid) {
        field.container.classList.remove("is-valid");
        field.container.classList.add("is-invalid");
        this.shadowRoot!.querySelector(`#${field.container.id}-hosted-field-error`)!.classList.remove('d-none');
        this.shadowRoot!.querySelector(`#${field.container.id}-hosted-field-error`)!.classList.add('d-block');
        isValid = false;
      }
      else {
        field.container.classList.remove("is-invalid");
        field.container.classList.add("is-valid");
        this.shadowRoot!.querySelector(`#${field.container.id}-hosted-field-error`)!.classList.remove('d-block');
        this.shadowRoot!.querySelector(`#${field.container.id}-hosted-field-error`)!.classList.add('d-none');
      }
    });
    return isValid;
  }

  /**
   * Validate credit card info. If valid then tokenize credit card form values and update payment details.
   */
  public async tokenizeCreditCard() {
    if(this.validateCreditCard()){
      const tokenizedPayloadPromise = this.braintreeHostedFieldsInstance!
        .tokenize()
        .catch(() => {
          store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
          throw new Error("Credit Card tokenization failed.");
        });
      if (this.creditCardUniqueId) {
        const billingAddress = getBillingAddress(store.getState() as StoreState);
         if(!isValidBillingAddress(billingAddress)) {
           throw new Error('Billing Address is needed');
         }
         
        // vaulted credit card
        if (this.threeDsEnabled) {
          await tokenizedPayloadPromise;
          if(this.creditCardUniqueId && this.bin) {
            await this.verifyExistingCard();
          } else {
            throw new Error("Credit Card tokenization failed.");
          }
        }
        const tokenizedPayload = await tokenizedPayloadPromise as HostedFieldsTokenizePayload;
        this.paymentDetails.creditCardUniqueIdentifier = this.creditCardUniqueId;
        this.paymentDetails.cvvNonce = tokenizedPayload.nonce;
      } else {
        const billingAddress = getBillingAddress(store.getState() as StoreState);
        if (!isValidBillingAddress(billingAddress)) {
          throw new Error('Complete all required billing and payment fields before continuing.');
        }
        
        // new credit card
        if (this.threeDsEnabled) {
          await tokenizedPayloadPromise.then( payload => {
            if (payload) {
              return this.verifyCard(payload);
            }
            throw new Error('Credit Card tokenization failed.');
          });
        }
        const tokenizedPayload = await tokenizedPayloadPromise as HostedFieldsTokenizePayload;
        this.creditCardDisplayInfo = {
          cardholderName: tokenizedPayload.details.cardholderName,
          cardType: tokenizedPayload.details.cardType,
          cardLast4: tokenizedPayload.details.lastFour,
          bin: tokenizedPayload.details.bin,
          expiryMonth: tokenizedPayload.details.expirationMonth,
          expiryYear: tokenizedPayload.details.expirationYear
        }
        if (!this.threeDsEnabled) {
          this.paymentDetails.paymentNonce = JSON.parse(JSON.stringify(tokenizedPayload.nonce));
        }
      }
    } else {
      throw new Error("Credit card information is not valid");
    }
  }

  public getCreditCardDisplayInfo(){
    return this.creditCardDisplayInfo;
  }

  /**
   * Get tokenized payment nonce and 3ds info.
   */
  public getPaymentDetails(){
    return this.paymentDetails;
  }

  /**
   * 3d Secure card verification step for existing credit card (CVV only).
   * @param tokenizedPayload 
   */
  private async verifyExistingCard(): Promise<ThreeDSecureVerifyPayload | void>{
    const amount = this._getTotalAmount();
    await this.braintreeThreeDSecureInstance!
      .verifyCard({
        amount: amount,
        nonce: this.vaultedCreditCardNonce,
        bin: this.bin,
      })
      .then((payload) => {
        if (payload.threeDSecureInfo.liabilityShifted) {
          this.paymentDetails.threedsAuthenticationId = payload.threeDSecureInfo.threeDSecureAuthenticationId;
          store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.VALID));
          return payload;
        }
        store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
        throw new Error("Credit Card tokenization failed.");
      })
      .catch((error) => {
        console.log("three ds error" + error);
        throw new Error("Credit Card tokenization failed."+error);
      });
  }

  /**
   * 3d Secure card verification step for new credit card.
   * @param tokenizedPayload 
   */
  private async verifyCard(tokenizedPayload: HostedFieldsTokenizePayload): Promise<ThreeDSecureVerifyPayload | void>{
    const amount = this._getTotalAmount();
    await this.braintreeThreeDSecureInstance!
      .verifyCard({
        amount: amount,
        nonce: tokenizedPayload.nonce,
        bin: tokenizedPayload.details.bin,
      })
      .then((payload) => {
        if (payload.threeDSecureInfo.liabilityShifted) {
          this.paymentDetails.paymentNonce = payload.nonce;
          store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.VALID));
          return payload;
        } else if(payload.rawCardinalSDKVerificationData.Payment.ExtendedData?.ChallengeCancel == "01") {
          throw this.threeDSCancelErrorMsg;
        }
        store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
        throw new Error("Credit Card tokenization failed.");
      })
      .catch((error) => {
        console.log("three ds error", error);
        if (error === this.threeDSCancelErrorMsg) {
          throw new Error(error);
        }
        store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
        throw new Error("Credit Card tokenization failed."+error);
      });
  }
  
  /**
   * If Braintree hosted fields is an existing credit card form (CVV only), it will have a credit card unique ID.
   * @returns 
   */
  public isVaultedCreditCardForm(){
    return this.creditCardUniqueId;
  }

  focusOn(field: CREDIT_CARD_FIELDS) {
    /*
     retry logic is introduce for the edge case, when the component is render then the CC hosted fields are created,
     which take time so this logic make sure atleast to check 5 times that hosted fields are created or not.
    */
    let retry = 5;
    const interval = setInterval(() => {
      if(this.braintreeHostedFieldsInstance) {
        this.braintreeHostedFieldsInstance.focus(field);
        clearInterval(interval);
      } else {
        if(retry <= 0) {
          clearInterval(interval);
        } else {
          retry--;
        }
      }
    }, 500);
  }

  _getTotalAmount() {
    if(isTunnelPurchaseFlow() && this.threeDsEnabled) {
      const cart = getCart(store.getState() as StoreState);
      let totalAmount = cart!.totalAmount;
      totalAmount += getIntlTaxAmount(store.getState() as StoreState)!;
      return Math.round(totalAmount * 100)/100;
    }
    return this.amount;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-credit-card-payment-hosted-fields': BraintreeHostedFields;
  }
}
